/* eslint-disable jsx-a11y/label-has-associated-control */
import Button from '@material-ui/core/Button';
import CheckIcon from '@material-ui/icons/Check';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import FlagsSelect from '@orientaction/components/CountryFlagSelector/FlagsSelect';
import MaterialPhoneInput from '@orientaction/components/MaterialPhoneInput';
import { isValidPhoneNumber } from 'libphonenumber-js';
import moment from 'moment';
import { Controller } from 'react-hook-form';
import RedesignImage from '../../../shared/components/RedesignImage';
import RedesignModal from '../../../shared/components/RedesignModal';
import { useProfileEditSharedStyles } from '../Edit.styles';
import { useProfileEditForm } from '../hooks/useProfileForm';
import locale from '../locale.json';
import { useProfileFormStyles } from './ProfileForm.styles';

type ProfileFormProps = {
  user: Record<string, any>;
  updateUser: (data: Record<string, any>) => Promise<any>;
  loading: boolean;
};

const ProfileForm = ({ user, updateUser, loading }: ProfileFormProps) => {
  const local = useProfileFormStyles();
  const shared = useProfileEditSharedStyles();
  const classes = { ...shared, ...local };
  const {
    control,
    errors,
    handleSubmit,
    selectedLanguage,
    onSelectLanguage,
    goBack,
    onSubmitHandler,
    deleteAccount,
    openDeleteModal,
    setOpenDeleteModal,
    isAdminUser,
  } = useProfileEditForm({ user, updateUser });

  if (!user?.email) {
    return null;
  }

  return (
    <>
      <form className={classes.form} onSubmit={handleSubmit(onSubmitHandler)}>
        <section className={classes.sectionCard}>
          <div className={classes.sectionHeader}>
            <div className={classes.sectionIconWrap}>
              <PersonOutlineIcon className={classes.sectionIcon} />
            </div>
            <h3 className={classes.sectionTitle}>{locale.personalInfoTitle}</h3>
          </div>
          <div className={classes.sectionBody}>
            <div className={classes.fieldRow}>
              <div className={classes.field}>
                <Controller
                  name="firstname"
                  control={control}
                  defaultValue={user?.firstname}
                  rules={{ required: 'Champ obligatoire' }}
                  render={({ field }) => (
                    <>
                      <label className={classes.label} htmlFor="firstname">
                        {locale.firstname}*
                      </label>
                      <input
                        {...field}
                        id="firstname"
                        type="text"
                        className={classes.input}
                        placeholder={locale.firstname}
                      />
                      {errors?.firstname?.message && (
                        <span className={classes.errorField}>
                          {String(errors.firstname.message)}
                        </span>
                      )}
                    </>
                  )}
                />
              </div>

              <div className={classes.field}>
                <Controller
                  name="lastname"
                  control={control}
                  defaultValue={user?.lastname}
                  rules={{ required: 'Champ obligatoire' }}
                  render={({ field }) => (
                    <>
                      <label className={classes.label} htmlFor="lastname">
                        {locale.lastname}*
                      </label>
                      <input
                        {...field}
                        id="lastname"
                        type="text"
                        className={classes.input}
                        placeholder={locale.lastname}
                      />
                      {errors?.lastname?.message && (
                        <span className={classes.errorField}>
                          {String(errors.lastname.message)}
                        </span>
                      )}
                    </>
                  )}
                />
              </div>
            </div>

            <div className={classes.field}>
              <Controller
                name="description"
                control={control}
                defaultValue={user?.description}
                rules={{ required: 'Champ obligatoire' }}
                render={({ field }) => (
                  <>
                    <label className={classes.label} htmlFor="description">
                      {locale.description}*
                    </label>
                    <textarea
                      {...field}
                      id="description"
                      className={classes.textarea}
                      placeholder={locale.description}
                      rows={4}
                    />
                    {errors?.description?.message && (
                      <span className={classes.errorField}>
                        {String(errors.description.message)}
                      </span>
                    )}
                  </>
                )}
              />
            </div>

            <div className={classes.field}>
              <Controller
                name="dayOfBirth"
                control={control}
                defaultValue={user.dayOfBirth ? moment(user.dayOfBirth).format('YYYY-MM-DD') : ''}
                rules={{ required: 'Champ obligatoire' }}
                render={({ field }) => (
                  <>
                    <label className={classes.label} htmlFor="dayOfBirth">
                      {locale.birthDate}*
                    </label>
                    <input {...field} id="dayOfBirth" type="date" className={classes.input} />
                    {errors?.dayOfBirth?.message && (
                      <span className={classes.errorField}>
                        {String(errors.dayOfBirth.message)}
                      </span>
                    )}
                  </>
                )}
              />
            </div>
          </div>
        </section>

        <section className={classes.sectionCard}>
          <div className={classes.sectionHeader}>
            <div className={classes.sectionIconWrap}>
              <EmailOutlinedIcon className={classes.sectionIcon} />
            </div>
            <h3 className={classes.sectionTitle}>{locale.contactTitle}</h3>
          </div>
          <div className={classes.sectionBody}>
            <div className={classes.fieldRow}>
              <div className={classes.field}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue={user?.email}
                  rules={{ required: 'Champ obligatoire' }}
                  render={({ field }) => (
                    <>
                      <label className={classes.label} htmlFor="email">
                        {locale.email}*
                      </label>
                      <input
                        {...field}
                        id="email"
                        type="email"
                        className={classes.input}
                        placeholder={locale.email}
                      />
                      {errors?.email?.message && (
                        <span className={classes.errorField}>{String(errors.email.message)}</span>
                      )}
                    </>
                  )}
                />
              </div>

              <div className={classes.field}>
                <Controller
                  name="post_code"
                  control={control}
                  defaultValue={user?.postal_code}
                  rules={{ required: 'Champ obligatoire' }}
                  render={({ field }) => (
                    <>
                      <label className={classes.label} htmlFor="post_code">
                        {locale.postalCode}*
                      </label>
                      <input
                        {...field}
                        id="post_code"
                        type="text"
                        className={classes.input}
                        placeholder={locale.postalCode}
                      />
                      {errors?.post_code?.message && (
                        <span className={classes.errorField}>
                          {String(errors.post_code.message)}
                        </span>
                      )}
                    </>
                  )}
                />
              </div>
            </div>

            <div className={classes.fieldRow}>
              <div className={classes.field}>
                <Controller
                  name="phone"
                  control={control}
                  defaultValue={user?.phone || ''}
                  rules={{
                    required: 'Champ obligatoire',
                    validate: {
                      isValid: value =>
                        isValidPhoneNumber(value || '') || 'Le format du numéro est invalide',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <>
                      <label className={classes.label} htmlFor="phone">
                        {locale.phone}*
                      </label>
                      <div className={classes.phoneField}>
                        <MaterialPhoneInput
                          value={field.value || ''}
                          onChange={field.onChange}
                          onCountryChange={field.onBlur}
                          error={fieldState.invalid}
                        />
                      </div>
                      {errors?.phone?.message && (
                        <span className={classes.errorField}>{String(errors.phone.message)}</span>
                      )}
                    </>
                  )}
                />
              </div>

              <div className={classes.field}>
                <label className={classes.label} htmlFor="language">
                  {locale.language}
                </label>
                <FlagsSelect
                  countries={['FR', 'GB']}
                  selected={selectedLanguage}
                  onSelect={onSelectLanguage}
                  classes={{ button: classes.languageSelectorButton }}
                />
              </div>
            </div>
          </div>
        </section>

        <p className={classes.requiredHint}>{locale.requiredFields}</p>

        {!isAdminUser && (
          <div className={classes.deleteSection}>
            <div className={classes.deleteContent}>
              <div className={classes.deleteIconWrap}>
                <RedesignImage src="/icons/Warning.svg" alt="" className={classes.deleteIcon} />
              </div>
              <div className={classes.deleteTextBlock}>
                <p className={classes.deleteTitle}>{locale.deleteTitle}</p>
                <p className={classes.deleteDescription}>{locale.deleteDescription}</p>
              </div>
            </div>
            <Button
              className={classes.deleteButton}
              onClick={() => setOpenDeleteModal(true)}
              startIcon={<DeleteOutlineIcon className={classes.deleteButtonIcon} />}>
              {locale.deleteButton}
            </Button>
          </div>
        )}

        <div className={classes.actions}>
          <Button type="button" className={classes.cancelButton} onClick={goBack}>
            {locale.cancel}
          </Button>
          <Button
            type="submit"
            className={classes.saveButton}
            disabled={loading}
            endIcon={!loading ? <CheckIcon /> : undefined}>
            {loading ? locale.saving : locale.save}
          </Button>
        </div>
      </form>

      <RedesignModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        labelledBy="delete-account-modal-title"
        paperClassName={classes.deleteModalPanel}>
        <h2 id="delete-account-modal-title" className={classes.deleteModalTitle}>
          {locale.deleteModalTitle}
        </h2>
        <p className={classes.deleteModalMessage}>{locale.deleteModalMessage}</p>
        <div className={classes.deleteModalActions}>
          <Button
            className={classes.deleteModalCancelButton}
            onClick={() => setOpenDeleteModal(false)}>
            {locale.deleteModalCancel}
          </Button>
          <Button className={classes.deleteModalConfirmButton} onClick={deleteAccount}>
            {locale.deleteModalConfirm}
          </Button>
        </div>
      </RedesignModal>
    </>
  );
};

export default ProfileForm;
